<script>
  import { onDestroy, getContext } from "svelte";
  import { Loader } from "@gl/design-system";
  import { user, language, contract, callout, url, Auth, _ } from "./stores/index";
  import { UsersRepository } from "./repositories/index";
  import { MicrofrontendError } from "./components/index";
  import { deriveMountedPath } from "./utils";
  import {
    loadMicrofrontendModule,
    reportLoadFailure,
  } from "./services/microfrontend";

  const BUNDLE_URL = "/cut-manager-frontend/cut-manager.es.js";

  const config = getContext("config");
  const authClass = new Auth(user, "cut-manager");

  const basePath = "/cut-manager";

  let path;
  $: {
    const mounted = deriveMountedPath($url.pathname, $url.search, basePath);
    if (mounted) path = mounted.full;
  }

  let el;
  let cutManager = null;
  let loading = false;
  let loadError = null;

  async function mountCutManager() {
    if (!el || !$contract || loading || cutManager) return;
    loading = true;
    loadError = null;
    try {
      const mod = await loadMicrofrontendModule(BUNDLE_URL, "cut-manager");

      // Imperative mount with a real target so Cut Manager's own Svelte runtime can
      // resolve $$.root. Context cannot cross that boundary, hence isAuthorized as
      // a prop.
      cutManager = new mod.default({
        target: el,
        props: {
          apiurl: config.API_URL,
          user: $user,
          language: $language,
          contract: $contract,
          basepath: "/cut-manager",
          path,
          isAuthorized: authClass,
          repositories: {
            getUsers: UsersRepository.getUsers.bind(null, $contract?.id),
            getGroups: UsersRepository.getGroups.bind(null, $contract?.id),
          },
        },
      });

      cutManager.$on("callout", (event) => callout.set(event.detail.callout));
    } catch (error) {
      // A failed import and a throwing constructor both land here.
      loadError = error;
      if (error?.name !== "MicrofrontendLoadError") {
        reportLoadFailure(error, "cut-manager");
      }
    } finally {
      loading = false;
    }
  }

  function retry() {
    loadError = null;
    mountCutManager();
  }

  $: if (el && $contract) mountCutManager();

  $: if (cutManager) {
    cutManager.$set({
      user: $user,
      language: $language,
      contract: $contract,
      path,
      repositories: {
        getUsers: UsersRepository.getUsers.bind(null, $contract?.id),
        getGroups: UsersRepository.getGroups.bind(null, $contract?.id),
      },
    });
  }

  onDestroy(() => cutManager?.$destroy());
</script>

<svelte:head>
  <title>{$_("cut-manager")}</title>
</svelte:head>

{#if $contract}
  {#if loadError}
    <MicrofrontendError
      app="cut-manager"
      retrying={loading}
      on:click={retry}
    />
  {:else if !cutManager && loading}
    <div class="mfe-loading">
      <Loader />
      <span>{$_("microfrontend.loading", { values: { app: $_("cut-manager") } })}</span>
    </div>
  {/if}
  <div bind:this={el} />
{/if}

<style>
  .mfe-loading {
    display: flex;
    gap: 12px;
    align-items: center;
  }
</style>
